import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '../../../layout/Icon.jsx';
import { Pill } from '../../../layout/Pill.jsx';
import { formatIQD, formatDateTime, formatDate } from '../../../shared/format.js';
import { apiError } from '../../../shared/api.js';
import { PrintLogo } from '../../../shared/PrintLogo.jsx';
import { ExportButton } from '../../reports/components/ExportButton.jsx';
import { useCardStatement } from '../hooks/useCards.js';
import { StatementExportButtons } from './StatementExport.jsx';
import './print.css';

/**
 * كشف حساب البطاقة — كل حركة غيّرت رصيدها منذ الإنشاء، مع الرصيد بعد كل حركة.
 *
 * الحركات *مشتقة* من المستندات الأصلية (الفواتير، حركات القاصات، دفعات
 * الديون، سجلّ التدقيق) لا من دفتر موازٍ، ولذلك لا يوجد في هذه الشاشة أي زرّ
 * تعديل أو حذف لحركة: التصحيح يتمّ بعملية مقابلة موثّقة (إلغاء فاتورة، عكس
 * دفعة، تعديل رصيد) فتظهر سطراً جديداً ويبقى السطر الأصلي كما وقع.
 */

const TYPE_FILTERS = [
  { key: '', label: 'كل الحركات' },
  { key: 'opening', label: 'الرصيد الابتدائي' },
  { key: 'topup', label: 'تعبئة الرصيد' },
  { key: 'purchase', label: 'عمليات الشراء' },
  { key: 'play_fee', label: 'رسوم اللعب والدخول' },
  { key: 'checkout', label: 'تسوية المحاسبة' },
  { key: 'subscription', label: 'الاشتراكات' },
  { key: 'debt_settle', label: 'تسديد الديون' },
  { key: 'refund', label: 'استرجاع المبالغ' },
  { key: 'adjustment', label: 'تعديل الرصيد' },
  { key: 'reconcile', label: 'فروق غير موثّقة' }
];

const KIND_TONE = {
  opening: 'info',
  topup: 'success',
  purchase: 'orange',
  play_fee: 'orange',
  checkout: 'orange',
  subscription: 'orange',
  debt_settle: 'orange',
  refund: 'success',
  adjustment: 'warning',
  reconcile: 'danger'
};

const STATUS_LABEL = {
  available: 'متاحة',
  active: 'مستخدمة',
  blocked: 'محظورة',
  lost: 'مفقودة'
};

function ownerLabel(owner) {
  if (!owner) return '—';
  const parts = [owner.childName, owner.guardianName].filter(Boolean);
  if (!parts.length) return 'غير مرتبطة بطفل';
  return parts.join(' / ');
}

/** خلية ملخّص — نفس شكل الـKPI في بقية الشاشات وتُطبع بإطار رمادي. */
function Kpi({ label, value, color, hint }) {
  return (
    <div className="card p-2.5 print-kpi">
      <div className="text-[11px] text-ink-3">{label}</div>
      <div className="text-[15px] font-extrabold mt-0.5" style={color ? { color } : undefined}>{value}</div>
      {hint ? <div className="text-[10.5px] text-ink-3">{hint}</div> : null}
    </div>
  );
}

/**
 * جسم الكشف — يُرسَم مرتين بنفس الكود: مرة داخل النافذة للشاشة، ومرة داخل
 * بوابة ملتصقة بـ`body` للطباعة (انظر التعليق في `print.css`: النافذة
 * `fixed` + `overflow:auto` كانت ستقصّ الورق على ورقة واحدة).
 */
function StatementBody({ data, rangeLabel, loading, error, forPrint }) {
  const card = data?.card;
  const totals = data?.totals || {};
  const movements = data?.movements || [];
  const hasRange = !!(data?.filters?.from || data?.filters?.to);

  return (
    // `data-statement` يفرّق النسختين (الشاشة/الورق) — نفس الكود يُرسَم مرتين،
    // والاختبارات والفحوص تحتاج أن تخاطب نسخة واحدة بلا التباس.
    <div className="print-root space-y-3" data-statement={forPrint ? 'print' : 'screen'}>
      {/* شعار المركز من الإعدادات — أعلى الورقة ووسطها */}
      <PrintLogo />
      {/* ترويسة رسمية تظهر على الورق فقط */}
      <div className="print-only print-section" style={{ textAlign: 'center', marginBottom: '6mm' }}>
        <h2 style={{ margin: 0, fontSize: '15pt', fontWeight: 800 }}>كشف حساب بطاقة</h2>
        <div style={{ fontSize: '10pt', marginTop: '1mm' }}>
          رقم البطاقة: {card?.uid || '—'}
          {' — '}
          {rangeLabel}
        </div>
        <div style={{ fontSize: '9pt', marginTop: '1mm' }}>
          أُصدر في: {formatDateTime(new Date())}
        </div>
      </div>

      {/* معلومات البطاقة */}
      <div className="grid grid-cols-4 gap-2 print-section">
        <Kpi label="رقم البطاقة" value={card?.uid || '—'} />
        <Kpi label="صاحب البطاقة" value={ownerLabel(data?.owner)} hint={data?.owner?.guardianPhone || ''} />
        <Kpi label="تاريخ إنشاء البطاقة" value={card?.createdAt ? formatDate(card.createdAt) : '—'} />
        <Kpi
          label="الرصيد الحالي"
          value={formatIQD(totals.currentBalance ?? card?.balance ?? 0)}
          color="#15803D"
          hint={card?.status ? STATUS_LABEL[card.status] || card.status : ''}
        />
      </div>

      {/* الإجماليات — فوق الجدول، ومطابقة للأسطر المسرودة تحته */}
      <div className="grid grid-cols-3 gap-2 print-section">
        <Kpi label="إجمالي المضاف (+)" value={formatIQD(totals.credit || 0)} color="#15803D" />
        <Kpi label="إجمالي المخصوم (−)" value={formatIQD(totals.debit || 0)} color="#B91C1C" />
        <Kpi
          label="الرصيد الحالي للبطاقة"
          value={formatIQD(totals.currentBalance || 0)}
          color="#1D4ED8"
          hint={totals.filtered
            ? `المعروض: ${formatIQD((totals.credit || 0) - (totals.debit || 0))} صافي حركات الفلتر`
            : 'المضاف − المخصوم = الرصيد الحالي'}
        />
      </div>

      {hasRange ? (
        <div className="grid grid-cols-2 gap-2 print-section">
          <Kpi label="الرصيد الافتتاحي للمدة" value={formatIQD(totals.openingBalance || 0)} />
          <Kpi label="الرصيد الختامي للمدة" value={formatIQD(totals.closingBalance || 0)} />
        </div>
      ) : null}

      {Array.isArray(data?.notices) && data.notices.length ? (
        <div className="print-section space-y-1">
          {data.notices.map((n, i) => (
            <div
              key={i}
              className="text-[11.5px] rounded-[8px] px-2.5 py-2 border"
              style={{ background: '#FFF7ED', color: '#92400E', borderColor: '#FED7AA' }}
            >
              {n}
            </div>
          ))}
        </div>
      ) : null}

      <div className={forPrint ? '' : 'overflow-x-auto'}>
        <table className="statement-table text-[12.5px]">
          <thead className="bg-paper-2 text-[11.5px] text-ink-3">
            <tr>
              <th className="p-2">التاريخ والوقت</th>
              <th className="p-2">نوع الحركة</th>
              <th className="p-2">البيان</th>
              <th className="p-2">المبلغ الدائن (+)</th>
              <th className="p-2">المبلغ المدين (−)</th>
              <th className="p-2">الرصيد بعد الحركة</th>
              <th className="p-2">المستخدم المنفّذ</th>
            </tr>
          </thead>
          <tbody>
            {hasRange ? (
              <tr style={{ background: '#FAF6EE' }}>
                <td className="p-2 font-bold" colSpan={5}>الرصيد الافتتاحي للمدة</td>
                <td className="p-2 font-extrabold">{formatIQD(totals.openingBalance || 0)}</td>
                <td className="p-2">—</td>
              </tr>
            ) : null}
            {loading ? (
              <tr><td colSpan={7} className="p-6 text-center text-ink-3">جاري التحميل...</td></tr>
            ) : error ? (
              <tr><td colSpan={7} className="p-6 text-center text-red font-semibold">{error}</td></tr>
            ) : !movements.length ? (
              <tr><td colSpan={7} className="p-6 text-center text-ink-3">لا حركات مطابقة في هذه المدة</td></tr>
            ) : movements.map((r, i) => (
              <tr key={`${r.kind}-${r.refId || r.document}-${i}`} className="border-t border-line">
                <td className="p-2 text-ink-3 whitespace-nowrap">{formatDateTime(r.at)}</td>
                <td className="p-2"><Pill variant={KIND_TONE[r.kind] || 'muted'}>{r.typeLabel}</Pill></td>
                <td className="p-2">
                  <div>{r.description || '—'}</div>
                  {r.document ? (
                    <div className="text-[10.5px] text-ink-3 font-mono">رقم العملية: {r.document}</div>
                  ) : null}
                </td>
                <td className="p-2 font-semibold whitespace-nowrap" style={{ color: r.credit ? '#15803D' : undefined }}>
                  {r.credit ? formatIQD(r.credit) : '—'}
                </td>
                <td className="p-2 font-semibold whitespace-nowrap" style={{ color: r.debit ? '#B91C1C' : undefined }}>
                  {r.debit ? formatIQD(r.debit) : '—'}
                </td>
                <td className="p-2 font-extrabold whitespace-nowrap">{formatIQD(r.balanceAfter || 0)}</td>
                <td className="p-2 text-ink-3">{r.userName || '—'}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ background: '#FAF6EE' }} className="font-extrabold">
              <td className="p-2" colSpan={3}>
                الإجماليات ({movements.length} حركة)
              </td>
              <td className="p-2" style={{ color: '#15803D' }}>{formatIQD(totals.credit || 0)}</td>
              <td className="p-2" style={{ color: '#B91C1C' }}>{formatIQD(totals.debit || 0)}</td>
              <td className="p-2">{formatIQD(hasRange ? (totals.closingBalance || 0) : (totals.currentBalance || 0))}</td>
              <td className="p-2">—</td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div className="text-[11px] text-ink-3 leading-relaxed print-section">
        حركات هذا الكشف مشتقّة من المستندات الأصلية (الفواتير، حركات القاصات، دفعات الديون،
        سجلّ التدقيق) ولا تُعدَّل ولا تُحذف من هنا. أي تصحيح يتمّ بعملية مقابلة موثّقة
        (إلغاء فاتورة، عكس دفعة، تعديل رصيد) فتظهر سطراً جديداً ويبقى السطر الأصلي كما وقع.
        {data?.reconciliation?.balanced
          ? ' مجموع الحركات يطابق رصيد البطاقة الحقيقي.'
          : ''}
      </div>
    </div>
  );
}

export function StatementModal({ card, onClose }) {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [type, setType] = useState('');
  const [q, setQ] = useState('');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('desc');
  const tableRef = useRef(null);

  const cardId = card ? (card._id || card.id) : null;

  const params = useMemo(() => {
    const p = {};
    if (from) p.from = from;
    if (to) p.to = to;
    if (type) p.type = type;
    if (search.trim()) p.q = search.trim();
    if (sort !== 'desc') p.sort = sort;
    return p;
  }, [from, to, type, search, sort]);

  const query = useCardStatement(cardId, params, !!cardId);
  const data = query.data;
  const movements = data?.movements || [];
  const totals = data?.totals || {};

  // Esc يغلق النافذة — نفس سلوك بقية نوافذ النظام.
  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!card) return null;

  const rangeLabel = (from || to)
    ? `المدة: من ${from || 'البداية'} إلى ${to || 'اليوم'}`
    : 'المدة: كل الحركات منذ إنشاء البطاقة';

  // ── أعمدة التصدير: نفس أعمدة الجدول مع فصل «رقم العملية» في عمود مستقل
  //    (أفضل للفرز والتصفية في Excel من دمجه في البيان).
  const columns = [
    { key: 'at', label: 'التاريخ والوقت', width: 18 },
    { key: 'typeLabel', label: 'نوع الحركة', width: 16 },
    { key: 'description', label: 'البيان', width: 46 },
    { key: 'document', label: 'رقم العملية', width: 16 },
    { key: 'credit', label: 'المبلغ الدائن (+)', width: 16, money: true },
    { key: 'debit', label: 'المبلغ المدين (−)', width: 16, money: true },
    { key: 'balanceAfter', label: 'الرصيد بعد الحركة', width: 18, money: true },
    { key: 'userName', label: 'المستخدم المنفّذ', width: 18 }
  ];

  // أرقام حقيقية في Excel (تُجمع وتُنسَّق كعملة)، ونصوص منسّقة في CSV/PDF.
  const excelRows = movements.map((r) => ({
    at: formatDateTime(r.at),
    typeLabel: r.typeLabel || '',
    description: r.description || '',
    document: r.document || '',
    credit: Number(r.credit || 0),
    debit: Number(r.debit || 0),
    balanceAfter: Number(r.balanceAfter || 0),
    userName: r.userName || ''
  }));
  const textRows = movements.map((r) => ({
    at: formatDateTime(r.at),
    typeLabel: r.typeLabel || '',
    description: r.description || '',
    document: r.document || '',
    credit: r.credit ? formatIQD(r.credit) : '',
    debit: r.debit ? formatIQD(r.debit) : '',
    balanceAfter: formatIQD(r.balanceAfter || 0),
    userName: r.userName || ''
  }));

  const infoLines = [
    `رقم البطاقة: ${data?.card?.uid || card.uid}`,
    `صاحب البطاقة: ${ownerLabel(data?.owner)}`,
    `تاريخ إنشاء البطاقة: ${data?.card?.createdAt ? formatDate(data.card.createdAt) : '—'}`,
    `الرصيد الحالي: ${formatIQD(totals.currentBalance || 0)}`,
    rangeLabel,
    type ? `نوع الحركة: ${(TYPE_FILTERS.find((t) => t.key === type) || {}).label}` : null,
    search.trim() ? `بحث: ${search.trim()}` : null
  ].filter(Boolean);

  const totalsLines = [
    [`إجمالي المضاف (+)`, Number(totals.credit || 0)],
    [`إجمالي المخصوم (−)`, Number(totals.debit || 0)],
    [`الرصيد الحالي للبطاقة`, Number(totals.currentBalance || 0)]
  ];
  if (from || to) {
    totalsLines.unshift([`الرصيد الافتتاحي للمدة`, Number(totals.openingBalance || 0)]);
    totalsLines.push([`الرصيد الختامي للمدة`, Number(totals.closingBalance || 0)]);
  }

  const totalsRow = [
    'الإجماليات', '', '', '',
    formatIQD(totals.credit || 0),
    formatIQD(totals.debit || 0),
    formatIQD((from || to) ? (totals.closingBalance || 0) : (totals.currentBalance || 0)),
    ''
  ];

  const baseName = `كشف-حساب-البطاقة-${(data?.card?.uid || card.uid || '').replace(/\s+/g, '-')}`;
  const title = `كشف حساب البطاقة ${data?.card?.uid || card.uid}`;
  const hasFilters = !!(from || to || type || search);
  const error = query.isError ? apiError(query.error) : '';

  const body = (
    <StatementBody
      data={data}
      rangeLabel={rangeLabel}
      loading={query.isLoading}
      error={error}
      forPrint={false}
    />
  );

  return (
    <>
      <div
        className="fixed inset-0 z-50 grid place-items-center p-4 no-print"
        style={{ background: 'rgba(15,16,18,0.5)' }}
      >
        <div
          className="bg-white rounded-[14px] shadow-card w-full overflow-hidden max-h-[94vh] flex flex-col"
          style={{ maxWidth: 1180 }}
        >
          <div className="card-h">
            <div className="flex-1">
              <h3 className="text-[15px] font-bold m-0">
                كشف حساب البطاقة <span className="font-mono">{data?.card?.uid || card.uid}</span>
              </h3>
              <div className="text-[11.5px] text-ink-3 mt-0.5">
                كل حركة غيّرت الرصيد منذ إنشاء البطاقة — للقراءة فقط
              </div>
            </div>
            <button type="button" className="text-ink-3 hover:text-ink" onClick={onClose} title="إغلاق">
              <Icon name="x" size={16} />
            </button>
          </div>

          <div className="p-4 space-y-3 overflow-y-auto">
            <div className="flex items-center gap-2 flex-wrap">
              <label className="text-[12px] text-ink-3">من</label>
              <input
                type="date"
                className="input"
                style={{ width: 150 }}
                data-testid="statement-from"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
              />
              <label className="text-[12px] text-ink-3">إلى</label>
              <input
                type="date"
                className="input"
                style={{ width: 150 }}
                data-testid="statement-to"
                value={to}
                onChange={(e) => setTo(e.target.value)}
              />
              <select
                className="input"
                style={{ width: 180 }}
                data-testid="statement-type"
                value={type}
                onChange={(e) => setType(e.target.value)}
              >
                {TYPE_FILTERS.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
              </select>
              <form
                className="flex items-center gap-1"
                onSubmit={(e) => { e.preventDefault(); setSearch(q); }}
              >
                <input
                  className="input"
                  style={{ width: 210 }}
                  data-testid="statement-q"
                  placeholder="بحث في البيان أو رقم العملية..."
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                />
                <button type="submit" className="btn" title="بحث"><Icon name="search" size={13} /></button>
              </form>
              <button
                type="button"
                className="btn"
                data-testid="statement-sort"
                onClick={() => setSort((s) => (s === 'desc' ? 'asc' : 'desc'))}
                title="عكس الترتيب الزمني"
              >
                <Icon name="clock" size={13} /> {sort === 'desc' ? 'الأحدث أولاً' : 'الأقدم أولاً'}
              </button>
              {hasFilters ? (
                <button
                  type="button"
                  className="btn"
                  data-testid="statement-clear"
                  onClick={() => { setFrom(''); setTo(''); setType(''); setQ(''); setSearch(''); }}
                >
                  <Icon name="x" size={13} /> مسح الفلاتر
                </button>
              ) : null}
              <div className="ms-auto flex items-center gap-2">
                <StatementExportButtons
                  rows={excelRows}
                  columns={columns}
                  filename={baseName}
                  title={title}
                  info={infoLines}
                  totals={totalsLines}
                  disabled={query.isLoading}
                />
                {/* زرّ التصدير الشامل (CSV/Excel/PDF عربي صحيح) — يُخفي نفسه
                    تلقائياً لمن لا يملك `reports.export`. */}
                <ExportButton
                  rows={textRows}
                  columns={columns}
                  filename={baseName}
                  title={title}
                  subtitle={`صاحب البطاقة: ${ownerLabel(data?.owner)}`}
                  meta={infoLines}
                  totalsRow={totalsRow}
                  orientation="landscape"
                  tableRef={tableRef}
                />
              </div>
            </div>

            <div ref={tableRef}>{body}</div>
          </div>

          <div className="card-h border-b-0 border-t flex gap-2">
            <button type="button" className="btn flex-1 justify-center" onClick={onClose}>إغلاق</button>
          </div>
        </div>
      </div>

      {/* نسخة الطباعة: ملتصقة بـ`body` لا داخل النافذة، وإلا قصّها ارتفاع
          النافذة على ورقة واحدة (انظر `print.css`). */}
      {createPortal(
        <div className="print-portal">
          <StatementBody
            data={data}
            rangeLabel={rangeLabel}
            loading={query.isLoading}
            error={error}
            forPrint
          />
        </div>,
        document.body
      )}
    </>
  );
}
